import { useMemo, useRef, useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { CoachOnboardingBriefing } from '../../types/opsBriefing';
import { toCardView, sortCards, summarize, CoachCardView } from './coachOnboarding';
import {
    resolveCoachActions,
    actionStateOf,
    withOptimistic,
    persistCoachAction,
    coachActionToast,
    CoachActionType,
} from './coachOnboardingActions';
import CoachOnboardingCard from './CoachOnboardingCard';
import OnboardingHero from './OnboardingHero';
import { NoCoachCard, GraduationCard } from './OnboardingStateCards';
import { MemberNeed, MemberActionTarget } from './coachOnboardingNav';

/**
 * 신규 센터의 「오늘의 브리핑」 — **화면 전체를 이것이 대체한다** (정책 §1).
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 통째로 바꾸나
 * ─────────────────────────────────────────────────────────────
 * 세팅이 안 끝난 센터에는 **분석할 회원 데이터가 없다.** 예약도 수강권도
 * 없으니 이탈 위험도 재등록 기회도 나올 수가 없고, 기존 브리핑을 그대로
 * 띄우면 원장님은 빈 화면을 본다.
 *
 * 그 센터에서 그날 할 일은 전부 세팅이다. 그래서 화면을 통째로 바꾼다 —
 * 곁다리로 얹는 것이 아니다.
 *
 * ─────────────────────────────────────────────────────────────
 * 🔴 「신규」라는 말은 화면에 안 나간다
 * ─────────────────────────────────────────────────────────────
 * 내부 라우팅 키일 뿐이다 (결정 ④ · 정책 §1). 원장님에게 등급을 매기는
 * 말이 되기 때문에, 화면에는 「센터 세팅 중」 같은 상태 서술만 쓴다.
 */

/** 졸업 카드를 이미 본 날 — 「하루 한 번 보여주고 접는다」 (정책 §6) */
const GRAD_KEY = 'opsOnboardingGraduationSeen';

const readGraduationSeen = (): string | null => {
    try {
        return window.localStorage.getItem(GRAD_KEY);
    } catch {
        return null;
    }
};

const markGraduationSeen = (date: string) => {
    try {
        window.localStorage.setItem(GRAD_KEY, date);
    } catch {
        /* 저장만 실패한다. 이번 화면은 그대로 뜬다 */
    }
};

type Props = {
    briefing: CoachOnboardingBriefing;
    dateLabel: string;
    today?: dayjs.Dayjs;
    onOpenMember?: (need: MemberNeed, target: MemberActionTarget) => void;
    onToast?: (text: string) => void;
    /**
     * 서버에 남기기 — **성공 여부를 돌려줘야 한다** (정책 §4-3).
     *
     * 성공하면 기기를 바꿔도 유지되고, 실패하면(구서버·404) 이 브라우저에만
     * 남는다. 화면은 그 차이를 문구로 밝힌다 — 다른 기기에서 다시 뜨는 것을
     * 보고 「저장이 안 된다」로 읽으면 안 된다.
     */
    onPersist?: (coachId: string, type: 'CHECK' | 'HIDE_TODAY') => Promise<boolean>;
    /**
     * 서버 저장이 **성공한 뒤** — 부르는 쪽이 서버 이력을 다시 읽는다. 다 읽으면 resolve,
     * 못 읽으면 reject. 이 화면은 그 결과를 보고 임시 값을 거둘지 정한다.
     */
    onSaved?: (coachId: string, type: CoachActionType) => Promise<void> | void;
};

const OnboardingBriefingScreen = ({
    briefing,
    dateLabel,
    today = dayjs(),
    onOpenMember,
    onToast,
    onPersist,
    onSaved,
}: Props) => {
    /*
     * 행동 이력은 서버가 주면 서버, 아니면 이 브라우저다. `bump` 는 로컬에
     * 남긴 뒤 다시 읽게 하는 값 — 로컬 저장소는 리액트가 못 보므로
     * 상태 하나로 갱신 시점을 만든다.
     */
    const [bump, setBump] = useState(0);
    /*
     * 이번 세션에 누른 것 — 서버 왕복 전에도 화면에 보여야 한다.
     * 저장이 아니라 **표시**다. 다음 조회에서 서버 값이 오면 대체된다.
     */
    const [pending, setPending] = useState<Array<{ coachId: string; type: 'CHECK' | 'HIDE_TODAY' }>>(
        []
    );
    const { states, serverBacked } = useMemo(() => {
        const base = resolveCoachActions(briefing.actions, today);
        return { states: withOptimistic(base.states, pending, today), serverBacked: base.serverBacked };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [briefing.actions, today, bump, pending]);

    const [gradSeen, setGradSeen] = useState<string | null>(() => readGraduationSeen());

    const cards: CoachCardView[] = useMemo(
        () => sortCards(briefing.coaches.map((c) => toCardView(c, today))),
        [briefing.coaches, today]
    );

    const summary = useMemo(() => summarize(briefing.coaches), [briefing.coaches]);

    /*
     * 가려 둔 카드는 오늘만 빠진다. **챙길 일 수도 같이 줄어야 한다** —
     * 화면에 없는 것을 세면 「4건」이라 적어 놓고 카드가 셋인 상태가 된다.
     */
    const visible = cards.filter((c) => !actionStateOf(states, c.row.coachId).hiddenToday);
    const chores = visible.filter((c) => c.chore);

    const setupDone = briefing.setupSteps.done >= briefing.setupSteps.total;
    const showGraduation = setupDone && gradSeen !== briefing.date;

    /*
     * 「봤다」는 **그린 뒤에** 기록한다 (정책 §6 — 하루 한 번).
     *
     * ⚠️ 렌더 도중에 기록하면 안 된다. 같은 렌더에서 상태가 바뀌어 카드가
     *    나타났다 사라지고, 원장님은 졸업 축하를 **한 번도 못 본다.**
     *    그리고 나서 다음 날부터 접힌다.
     */
    useEffect(() => {
        if (showGraduation) {
            markGraduationSeen(briefing.date);
            setGradSeen(briefing.date);
        }
    }, [showGraduation, briefing.date]);

    /* 저장 중인 표시(강사|종류) — 연타를 한 번으로 만든다 */
    const inFlight = useRef<Set<string>>(new Set());
    /* 저장이 끝난 시점의 서버 모양 — 클릭 시점 값이 아니라 최신값을 읽는다 */
    const serverBackedRef = useRef(serverBacked);
    serverBackedRef.current = serverBacked;
    const act = async (coachId: string, type: 'CHECK' | 'HIDE_TODAY', name: string) => {
        const key = `${coachId}|${type}`;
        if (inFlight.current.has(key)) return;
        inFlight.current.add(key);
        /*
         * 🔴 **누른 것은 즉시 보인다.** 서버 왕복을 기다리면 그 사이에 화면이
         *    그대로라 원장님이 두 번 누른다. 표시부터 하고 저장은 뒤에 한다.
         */
        setPending((p) => [...p, { coachId, type }]);
        setBump((n) => n + 1);
        const dropPending = () => {
            setPending((p) => p.filter((x) => !(x.coachId === coachId && x.type === type)));
            setBump((n) => n + 1);
        };

        /*
         * 서버에 남긴다. 성공하면 기기를 바꿔도 유지된다.
         * 실패하면(구서버·404) 이 브라우저에만 남기고 **그 한계를 밝힌다.**
         *
         * ⚠️ 서버 성공 시에는 로컬에 남기지 않는다 — 두 곳에 남기면 서버에서
         *    지운 뒤에도 이 브라우저에서만 살아 있는 유령이 생긴다.
         *
         * 임시 값의 수명은 `OpsBriefingTodayPage.actOnCoach` 와 같다: 저장 성공이면
         * 서버 이력을 다시 읽은 뒤 거두고(정본으로 갈아탐), 실패면 바로 거둔다.
         */
        try {
            const { saved, note } = await persistCoachAction({
                coachId,
                type,
                today,
                serverBacked: () => serverBackedRef.current,
                persist: onPersist,
            });
            if (saved) {
                try {
                    await onSaved?.(coachId, type);
                    dropPending();
                } catch {
                    /* 서버 이력을 못 읽음 — 임시 값을 남겨 둔다 */
                }
            } else {
                dropPending();
            }
            onToast?.(coachActionToast(name, type, note));
        } finally {
            inFlight.current.delete(key);
        }
    };

    return (
        <div className="flex w-full flex-col">
            <OnboardingHero
                dateLabel={dateLabel}
                choreCount={chores.length}
                setupSteps={briefing.setupSteps}
                blockedAt={briefing.blockedAt}
                membersAnalyzing={briefing.membersAnalyzing}
                summary={summary}
            />

            <div className="flex items-baseline" style={{ gap: 10, margin: '22px 0 10px' }}>
                <b style={{ fontSize: 16, fontWeight: 800, color: '#16202E' }}>오늘 챙길 일</b>
                <span style={{ fontSize: 12.5, color: '#7C8794', fontWeight: 600 }}>
                    강사별로 묶었습니다
                </span>
            </div>

            {/*
              상태 B — 세팅 완료 졸업 카드. 하루 한 번 보여주고 접는다.
              카드를 그린 그 순간 「봤다」로 기록한다 — 다시 그리지 않기 위해서다.
            */}
            {showGraduation && (
                <div>
                    <GraduationCard
                        coaches={summary.coaches}
                        members={summary.members}
                        withPass={
                            summary.noPass == null ? null : Math.max(0, summary.members - summary.noPass)
                        }
                        analyzing={briefing.membersAnalyzing.count}
                        appMissing={summary.appMissing}
                        journal={journalTotalOf(briefing)}
                    />
                </div>
            )}

            {/*
              상태 A — 강사 0명. **이 카드 하나만** 보인다 (정책 §6).
              강사가 없으면 아래 카드도 있을 수 없으므로 자연히 하나만 남는다.
            */}
            {briefing.coaches.length === 0 && <NoCoachCard />}

            {visible.map((view, i) => (
                <CoachOnboardingCard
                    key={view.row.coachId}
                    view={view}
                    /* 번호는 **챙길 일 안에서만** 매긴다 — 순항 카드는 「—」다 */
                    index={chores.findIndex((c) => c.row.coachId === view.row.coachId)}
                    action={actionStateOf(states, view.row.coachId)}
                    serverBacked={serverBacked}
                    onCheck={(id) => act(id, 'CHECK', view.row.name)}
                    onHide={(id) => act(id, 'HIDE_TODAY', view.row.name)}
                    onOpenMember={onOpenMember}
                />
            ))}

            {/*
              가린 카드가 있으면 **그 사실을 밝힌다.** 안 밝히면 원장님이
              자기가 가려 놓고 「오늘은 조용하네」로 읽는다.
            */}
            {visible.length < cards.length && (
                <div
                    style={{
                        fontSize: 12.5,
                        color: '#7C8794',
                        fontWeight: 600,
                        padding: '4px 2px 0',
                    }}
                >
                    오늘만 가려 둔 카드 {cards.length - visible.length}장이 있습니다 · 내일 아침 다시
                    보입니다
                </div>
            )}
        </div>
    );
};

/** 졸업 카드의 일지 칸 — 전 강사 합계. 하나라도 못 쟀으면 null (P3) */
const journalTotalOf = (b: CoachOnboardingBriefing): { written: number; total: number } | null =>
    b.coaches.reduce<{ written: number; total: number } | null>((acc, c) => {
        if (acc == null || c.journal7d == null) return null;
        return {
            written: acc.written + c.journal7d.written,
            total: acc.total + c.journal7d.total,
        };
    }, { written: 0, total: 0 });

export default OnboardingBriefingScreen;
